import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  jsonb,
  decimal,
  timestamp,
} from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  email: text("email").unique(),
  pendingEmail: text("pending_email"), // Added for email change verification
  password: text("password").notNull(),
  sessionToken: text("session_token"),
  role: text("role").notNull().default("player"), // Available roles: "player", "admin", "manager"
  isActive: boolean("is_active").notNull().default(true),
  isVerified: boolean("is_verified").notNull().default(false),
  verificationToken: text("verification_token"),
  resetPasswordToken: text("reset_password_token"),
  resetPasswordExpires: timestamp("reset_password_expires"),
  // User membership type
  membershipType: text("membership_type").notNull().default("free"), // free, premium, excuse, exclusive
  membershipExpiry: timestamp("membership_expiry"), // For time-limited memberships
  // Firebase authentication fields
  googleId: text("google_id").unique(), // Google Firebase UID
  avatarUrl: text("avatar_url"), // URL to user's avatar (from Google)
  lastLogin: timestamp("last_login"), // Last login timestamp
  createdAt: timestamp("created_at").notNull().default(new Date()),
  updatedAt: timestamp("updated_at").notNull().default(new Date()),
  // Account deletion fields
  isDeleted: boolean("is_deleted").notNull().default(false),
  markedForDelete: boolean("marked_for_delete").notNull().default(false),
  deleteDate: timestamp("delete_date"),
  deletedDate: timestamp("deleted_date"),
});

// Define the player type for game rooms
export const gamePlayerSchema = z.object({
  id: z.number(),
  username: z.string(),
});

export const gameRounds = pgTable("game_rounds", {
  id: serial("id").primaryKey(),
  gameId: integer("game_id")
    .notNull()
    .references(() => gameRooms.id),
  isCurrentRound: boolean("is_current_round").notNull().default(false),
  startingDeck: jsonb("starting_deck").notNull().default("[]"),
  currentDeck: jsonb("current_deck").notNull().default("[]"),
  winner: integer("winner").references(() => users.id),
  isCompleted: boolean("is_completed").notNull().default(false),
  isCleanedUp: boolean("is_cleaned_up").notNull().default(false),
  player1: integer("player1").references(() => users.id),
  player2: integer("player2").references(() => users.id),
  player3: integer("player3").references(() => users.id),
  player4: integer("player4").references(() => users.id),
  myTurn: integer("my_turn").notNull().default(1),
  player1Score: integer("player1_score").notNull().default(0),
  player2Score: integer("player2_score").notNull().default(0),
  player3Score: integer("player3_score").notNull().default(0),
  player4Score: integer("player4_score").notNull().default(0),
  currentCard: text("current_card"),
  player1Deck: jsonb("player1_deck").notNull().default("[]"),
  player2Deck: jsonb("player2_deck").notNull().default("[]"),
  player3Deck: jsonb("player3_deck").notNull().default("[]"),
  player4Deck: jsonb("player4_deck").notNull().default("[]"),
  player1Played: jsonb("player1_played").notNull().default("[]"),
  player2Played: jsonb("player2_played").notNull().default("[]"),
  player3Played: jsonb("player3_played").notNull().default("[]"),
  player4Played: jsonb("player4_played").notNull().default("[]"),
  gameplaySequence: jsonb("gameplay_sequence").notNull().default("[]"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  underTenReplacement: jsonb("under_ten_replacement").notNull().default("[]"),
});

export const gameRooms = pgTable("game_rooms", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  creator: integer("creator")
    .notNull()
    .references(() => users.id),
  player1: integer("player1").references(() => users.id),
  player2: integer("player2").references(() => users.id),
  winner: integer("winner").references(() => users.id),
  lastRoundWinner: integer("last_round_winner").references(() => users.id),
  numberOfRounds: integer("number_of_rounds").notNull().default(0),
  isCompleted: boolean("is_completed").notNull().default(false),
  isStarted: boolean("is_started").notNull().default(false),
  player1Score: integer("player1_score").notNull().default(0),
  player2Score: integer("player2_score").notNull().default(0),
  spectatorMode: text("spectator_mode").notNull().default("private"),
  gameType: text("game_type").notNull().default("public"),
  friendId: integer("friend_id").references(() => users.id),
  tournamentId: integer("tournament_id").references(() => tournaments.id),
  matchId: integer("match_id").references(() => tournamentMatches.id),
  createdAt: text("created_at").notNull().default(new Date().toISOString()),
  startedAt: text("started_at"),
  completedAt: text("completed_at"),
  isDeleted: boolean("is_deleted").notNull().default(false),
  lastActivity: timestamp("last_activity").defaultNow(),
  endReason: text("end_reason").default("played"),
  isConsolidated: boolean("is_consolidated").notNull().default(false),
  isRoundsChecked: boolean("is_rounds_checked").notNull().default(false),
  dailyUpdateCounter: integer("daily_update_counter").default(0),
  gameSettings: jsonb("game_settings").notNull().default({
    aiDifficulty: {
      beginner: 1,
      pro: 7,
      default: 7,
    },
    shuffleType: "half",
    deckType: "sixToKing",
    gbaaProtection: true,
    gbaaPenalty: {
      canCall: false,
      true: 3,
      false: 2,
    },
    winnerStart: false,
    dryMode: false,
    underTenEnabled: true,
    gameScore: {
      numberOfRounds: 10,
      pointsToWin: 10,
      direction: "asc",
    },
    searchTimeOut: 60000,
    timer: {
      timerEnabled: false,
      timerDuration: 20,
      timerWarning: 5,
      timerWarningEnabled: true,
    },
    inactivityTimeoutTime: 600000,
  }),
});

export const insertUserSchema = z.object({
  username: z.string().min(3).max(20),
  email: z.string().email(),
  password: z.string().min(6),
  confirmPassword: z.string().min(6).optional(),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
}).refine((data) => {
  if (data.confirmPassword) {
    return data.password === data.confirmPassword;
  }
  return true;
}, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export const insertGameRoomSchema = createInsertSchema(gameRooms)
  .pick({
    name: true,
    creator: true,
  })
  .extend({
    name: z
      .string()
      .min(1, "Game name is required")
      .max(50, "Game name too long"),
    gameType: z.enum(["public", "friends", "private", "ai", "tournament"], {
      required_error: "Game type is required",
    }),
    spectatorMode: z.enum(["private", "public", "friends", "tournament"], {
      required_error: "Spectator mode is required",
    }),
    friendId: z.number().nullable().optional(),
    tournamentId: z.number().nullable().optional(),
    matchId: z.number().nullable().optional(),
  });

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type GameRoom = typeof gameRooms.$inferSelect;
export type GameRound = typeof gameRounds.$inferSelect;

export const friends = pgTable("friends", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  friendId: integer("friend_id")
    .notNull()
    .references(() => users.id),
  status: text("status").notNull().default("pending"),
  createdAt: text("created_at").notNull().default(new Date().toISOString()),
});

export const insertFriendSchema = createInsertSchema(friends).pick({
  userId: true,
  friendId: true,
});

export const userProfiles = pgTable("user_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  displayName: text("display_name"),
  nickname: text("nickname"),
  gamesPlayed: integer("games_played").notNull().default(0),
  gamesWon: integer("games_won").notNull().default(0),
  totalScore: integer("total_score").notNull().default(0),
  highestScore: integer("highest_score").notNull().default(0),
  winRate: decimal("win_rate", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  currency: integer("currency").notNull().default(0), // In-game currency balance
  premiumCurrency: integer("premium_currency").notNull().default(0), // Premium currency for special items
  dailyRewardStreak: integer("daily_reward_streak").notNull().default(0), // Number of consecutive days claimed
  lastDailyRewardClaim: timestamp("last_daily_reward_claim"), // Date of last reward claim
  level: integer("level").notNull().default(1), // User level
  xp: integer("xp").notNull().default(0), // Experience points
  lastPlayed: text("last_played"),
  avatarUrl: text("avatar_url"),
  bannerUrl: text("banner_url"),
  avatarId: integer("avatar_id"),
  bannerId: integer("banner_id"),
  avatarSource: text("avatar_source").notNull().default("default"),
  bannerSource: text("banner_source").notNull().default("default"),
  tutorialCompleted: boolean("tutorial_completed").notNull().default(false), // Track if the user completed the tutorial
  createdAt: text("created_at").notNull().default(new Date().toISOString()),
  updatedAt: text("updated_at").notNull().default(new Date().toISOString()),
});

export const insertUserProfileSchema = createInsertSchema(userProfiles).pick({
  userId: true,
});

export type InsertGameRoom = z.infer<typeof insertGameRoomSchema>;
export type InsertFriend = z.infer<typeof insertFriendSchema>;
export type Friend = typeof friends.$inferSelect;
export type UserProfile = typeof userProfiles.$inferSelect;
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export const spectators = pgTable("spectators", {
  id: serial("id").primaryKey(),
  gameId: integer("game_id")
    .notNull()
    .references(() => gameRooms.id),
  viewers: jsonb("viewers").notNull().default("[]"), // List of all historical viewers
  currentViewers: jsonb("current_viewers").notNull().default("[]"), // List of current active viewers
  createdAt: text("created_at").notNull().default(new Date().toISOString()),
  updatedAt: text("updated_at").notNull().default(new Date().toISOString()),
});

export const userSettings = pgTable("user_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  gameTheme: text("game_theme").notNull().default("default"),
  deckDesign: text("deck_design").notNull().default("default"),
  createdAt: text("created_at").notNull().default(new Date().toISOString()),
  updatedAt: text("updated_at").notNull().default(new Date().toISOString()),
  lastActivity: timestamp("last_activity").notNull().defaultNow(), // Added table with timestamp field
});

export const cardDecks = pgTable("card_decks", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().default(""),
  membership: text("membership").notNull().default("free"), // free, premium, exclusive
  deckUrl: text("deck_url").notNull().default(""), // Default to empty, will be set during folder initialization
  deckType: text("deck_type").notNull().default("common"), // common, rare, legendary, etc. 
  isTradeable: boolean("is_tradeable").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  managerMode: boolean("manager_mode").notNull().default(false), // Only visible to managers even when active
  price: decimal("price", { precision: 10, scale: 2 }).notNull().default("0"),
  currencyType: text("currency_type").notNull().default("regular"), // regular or premium
  expiryDate: text("expiry_date"),
  createdAt: text("created_at").notNull().default(new Date().toISOString()),
  updatedAt: text("updated_at").notNull().default(new Date().toISOString()),
});

export const insertCardDeckSchema = createInsertSchema(cardDecks)
  .pick({
    name: true,
  })
  .extend({
    name: z.string().min(1, "Deck name is required").max(50, "Deck name too long"),
    deckUrl: z.string().default(""), // Optional with empty string default
    membership: z.enum(["free", "premium", "exclusive"]).default("free"),
    deckType: z.enum(["common", "uncommon", "rare", "mythical", "legendary", "arcana"]).default("common"),
    isTradeable: z.boolean().default(false),
    isActive: z.boolean().default(false),
    managerMode: z.boolean().default(false), // Add managerMode field with default false
    price: z.number().default(0),
    currencyType: z.enum(["regular", "premium"]).default("regular"),
    expiryDate: z.string().optional().nullable(),
  });

export type Spectator = typeof spectators.$inferSelect;
export type GamePlayer = z.infer<typeof gamePlayerSchema>;
export type UserSettings = typeof userSettings.$inferSelect;
export const userDecks = pgTable("user_decks", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  cardDeckId: integer("card_deck_id")
    .notNull()
    .references(() => cardDecks.id),
  dateAcquired: text("date_acquired")
    .notNull()
    .default(new Date().toISOString()),
  isActive: boolean("is_active").notNull().default(false),
  isDefault: boolean("is_default").notNull().default(false),
  createdAt: text("created_at").notNull().default(new Date().toISOString()),
  updatedAt: text("updated_at").notNull().default(new Date().toISOString()),
});

export type CardDeck = typeof cardDecks.$inferSelect;
export type UserDeck = typeof userDecks.$inferSelect;
export type InsertCardDeck = z.infer<typeof insertCardDeckSchema>;
export type Tournament = typeof tournaments.$inferSelect;
export type TournamentInvitation = typeof tournamentInvitations.$inferSelect;
export type TournamentMatch = typeof tournamentMatches.$inferSelect;
export type Conversation = typeof conversations.$inferSelect;
export type Message = typeof messages.$inferSelect;
export type InsertTournament = z.infer<typeof insertTournamentSchema>;
export type InsertTournamentInvitation = z.infer<
  typeof insertTournamentInvitationSchema
>;

export const achievements = pgTable("achievements", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(), // game_milestones, skill_based, social
  requirement: integer("requirement").notNull(),
  imageUrl: text("image_url"),
  rewardAmount: integer("reward_amount").notNull().default(50),
  rewardType: text("reward_type").notNull().default("standard"), // standard or premium
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const userAchievements = pgTable("user_achievements", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  achievementId: integer("achievement_id")
    .notNull()
    .references(() => achievements.id),
  progress: integer("progress").notNull().default(0),
  completed: boolean("completed").notNull().default(false),
  rewardClaimed: boolean("reward_claimed").notNull().default(false),
  completedAt: timestamp("completed_at"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const tournaments = pgTable("tournaments", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  creator: integer("creator")
    .notNull()
    .references(() => users.id),
  startDate: timestamp("start_date"),
  endDate: timestamp("end_date"),
  status: text("status").notNull().default("pending"), // pending, active, completed, cancelled
  maxPlayers: integer("max_players").notNull().default(4),
  participants: jsonb("participants").notNull().default("[]"), // Array of user IDs
  matches: jsonb("matches").notNull().default("[]"), // Array of match configurations
  bracketData: jsonb("bracket_data").notNull().default("{}"), // Tournament bracket structure
  currentRound: integer("current_round").notNull().default(0),
  winnerUserId: integer("winner_user_id").references(() => users.id),
  rules: text("rules"), // Tournament rules
  prizeDetails: text("prize_details"), // Prize information
  additionalInfo: text("additional_info"), // Any other relevant tournament information
  gameType: text("game_type"), // Type of game for this tournament
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertTournamentSchema = createInsertSchema(tournaments)
  .pick({
    name: true,
    description: true,
  })
  .extend({
    name: z
      .string()
      .min(1, "Tournament name is required")
      .max(50, "Tournament name too long"),
    description: z.string().max(500).optional(),
    maxPlayers: z.number().min(4).max(64),
    rules: z.string().optional(),
    prizeDetails: z.string().optional(),
    additionalInfo: z.string().optional(),
    gameType: z.string().optional(),
  });

export const tournamentInvitations = pgTable("tournament_invitations", {
  id: serial("id").primaryKey(),
  tournamentId: integer("tournament_id")
    .notNull()
    .references(() => tournaments.id),
  inviterId: integer("inviter_id")
    .notNull()
    .references(() => users.id),
  inviteeId: integer("invitee_id")
    .notNull()
    .references(() => users.id),
  status: text("status").notNull().default("pending"), // pending, accepted, declined
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertTournamentInvitationSchema = createInsertSchema(
  tournamentInvitations,
).pick({
  tournamentId: true,
  inviteeId: true,
});

export const tournamentMatches = pgTable("tournament_matches", {
  id: serial("id").primaryKey(),
  tournamentId: integer("tournament_id")
    .notNull()
    .references(() => tournaments.id),
  gameRoomId: integer("game_room_id"),
  player1Id: integer("player1_id")
    .notNull()
    .references(() => users.id),
  player2Id: integer("player2_id")
    .notNull()
    .references(() => users.id),
  winnerId: integer("winner_id").references(() => users.id),
  round: integer("round").notNull(),
  matchNumber: integer("match_number").notNull(),
  status: text("status").notNull().default("pending"), // pending, active, completed
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Conversation table for direct messaging between users
export const conversations = pgTable("conversations", {
  id: serial("id").primaryKey(),
  user1Id: integer("user1_id")
    .notNull()
    .references(() => users.id),
  user2Id: integer("user2_id")
    .notNull()
    .references(() => users.id),
  lastMessageAt: timestamp("last_message_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Messages table for storing individual messages
export const messages = pgTable("messages", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id")
    .notNull()
    .references(() => conversations.id),
  senderId: integer("sender_id")
    .notNull()
    .references(() => users.id),
  receiverId: integer("receiver_id")
    .notNull()
    .references(() => users.id),
  content: text("content").notNull(),
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Insert schemas for conversation and messages
export const insertConversationSchema = createInsertSchema(conversations).pick({
  user1Id: true,
  user2Id: true,
});

export const insertMessageSchema = createInsertSchema(messages).pick({
  conversationId: true,
  senderId: true,
  receiverId: true,
  content: true,
}).extend({
  content: z.string().min(1, "Message content cannot be empty").max(2000, "Message is too long"),
});

// Currency transactions table for tracking currency flow
export const currencyTransactions = pgTable("currency_transactions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  amount: integer("amount").notNull(), // Positive for earned, negative for spent
  currencyType: text("currency_type").notNull().default("regular"), // regular or premium
  transactionType: text("transaction_type").notNull(), // game_win, daily_bonus, purchase, achievement_reward, etc.
  description: text("description").notNull(),
  itemId: integer("item_id"), // Optional reference to a shop item if relevant
  balanceAfter: integer("balance_after").notNull(), // Balance after this transaction
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Coin packages for topup
export const coinPackages = pgTable("coin_packages", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  amount: integer("amount").notNull(), // Payment amount in currency (e.g., GHS)
  coins: integer("coins").notNull(), // Number of coins provided by the package
  coinType: text("coin_type").notNull().default("regular"), // regular or premium
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Pending payments for Paystack integration
export const pendingPayments = pgTable("pending_payments", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  reference: text("reference").notNull().unique(), // Unique payment reference
  amount: integer("amount").notNull(), // Payment amount in currency
  coins: integer("coins").notNull(), // Number of coins to credit on success
  coinType: text("coin_type").notNull().default("regular"), // regular or premium
  status: text("status").notNull().default("pending"), // pending, completed, failed
  email: text("email").notNull(), // User's email for payment
  metadata: text("metadata"), // Additional payment metadata
  paymentDetails: text("payment_details"), // Payment response details
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Shop items table
export const shopItems = pgTable("shop_items", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  imageUrl: text("image_url"),
  audioUrl: text("audio_url"), // URL to the sound file
  soundType: text("sound_type"), // card-play, win, lose, background, etc.
  price: integer("price").notNull(),
  currencyType: text("currency_type").notNull().default("regular"), // regular or premium
  itemType: text("item_type").notNull(), // avatar, deck, banner, booster, sound, etc.
  category: text("category").notNull().default("decoration"), // decoration, appearance, enhancement, sound, etc.
  isActive: boolean("is_active").notNull().default(true),
	managerMode: boolean("manager_mode").default(false).notNull(),
  stock: integer("stock"), // null means unlimited
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const shopItemsRelations = relations(shopItems, ({ many, one }) => ({
  purchases: many(userPurchases),
  defaultBanner: one(defaultBanners, {
    fields: [shopItems.id],
    references: [defaultBanners.shopItemId],
  }),
}));

// User purchases table for tracking owned items
export const userPurchases = pgTable("user_purchases", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  shopItemId: integer("shop_item_id")
    .notNull()
    .references(() => shopItems.id),
  purchaseDate: timestamp("purchase_date").notNull().defaultNow(),
  isActive: boolean("is_active").notNull().default(true),
  expiryDate: timestamp("expiry_date"), // For limited-time items
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const userPurchasesRelations = relations(userPurchases, ({ one }) => ({
  item: one(shopItems, {
    fields: [userPurchases.shopItemId],
    references: [shopItems.id],
  }),
  user: one(users, {
    fields: [userPurchases.userId],
    references: [users.id],
  }),
}));

// Daily rewards configuration
export const dailyRewards = pgTable("daily_rewards", {
  id: serial("id").primaryKey(),
  day: integer("day").notNull(), // Day 1, 2, 3, etc.
  amount: integer("amount").notNull(), // Amount of currency
  currencyType: text("currency_type").notNull().default("regular"), // regular or premium
  description: text("description").notNull(),
  imageUrl: text("image_url"),
  isActive: boolean("is_active").notNull().default(true),
  bonusItemId: integer("bonus_item_id").references(() => shopItems.id), // Optional bonus item
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Insert schemas
export const insertCurrencyTransactionSchema = createInsertSchema(currencyTransactions).pick({
  userId: true,
  amount: true,
  currencyType: true,
  transactionType: true,
  description: true,
  itemId: true,
  balanceAfter: true,
});

export const insertShopItemSchema = createInsertSchema(shopItems).pick({
  name: true,
  description: true,
  imageUrl: true,
  audioUrl: true,
  soundType: true,
  price: true,
  currencyType: true,
  itemType: true,
  category: true,
  isActive: true,
  managerMode: true,
  stock: true,
}).extend({
  name: z.string().min(1, "Item name is required").max(50, "Item name too long"),
  description: z.string().min(1, "Description is required"),
  imageUrl: z.string().min(1, "Image URL is required"),
  itemType: z.enum(["avatar", "deck", "banner", "booster", "sound", "board"]).default("banner"),
  category: z.enum(["decoration", "appearance", "enhancement", "sound"]).default("appearance"),
  price: z.number().min(0),
  currencyType: z.enum(["regular", "premium"]).default("regular"),
  isActive: z.boolean().default(true),
  managerMode: z.boolean().default(false),
  stock: z.number().nullable(),
});

export const insertUserPurchaseSchema = createInsertSchema(userPurchases).pick({
  userId: true,
  shopItemId: true,
  isActive: true,
  expiryDate: true,
});

export const insertDailyRewardSchema = createInsertSchema(dailyRewards).pick({
  day: true,
  amount: true,
  currencyType: true,
  description: true,
  imageUrl: true,
  isActive: true,
  bonusItemId: true,
});

export const insertCoinPackageSchema = createInsertSchema(coinPackages).pick({
  name: true,
  description: true,
  amount: true,
  coins: true,
  coinType: true,
  isActive: true,
});

// Types
// Support ticket table for user support requests
export const supportTickets = pgTable("support_tickets", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  subject: text("subject").notNull(),
  description: text("description").notNull(),
  status: text("status").notNull().default("open"), // open, in_progress, closed
  priority: text("priority").notNull().default("medium"), // low, medium, high
  category: text("category").notNull().default("other"), // profile, account, coin_purchase, gameplay, bugs, other
  adminResponse: text("admin_response"),
  feedbackType: text("feedback_type"), // positive, negative
  feedbackContent: text("feedback_content"), // User's detailed feedback
  imageUrls: jsonb("image_urls").default([]), // Array of image URLs
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  closedAt: timestamp("closed_at"),
});

export const insertSupportTicketSchema = createInsertSchema(supportTickets)
  .pick({
    userId: true,
    subject: true,
    description: true,
    category: true,
  })
  .extend({
    subject: z.string().min(5, "Subject is required").max(100, "Subject too long"),
    description: z.string().min(10, "Description is required").max(1000, "Description too long"),
    category: z.enum(["profile", "account", "coin_purchase", "gameplay", "bugs", "other"]).default("other"),
  });

export const cardBacks = pgTable("card_backs", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  imageUrl: text("image_url").notNull(),
  price: decimal("price", { precision: 10, scale: 2 }).notNull().default("0"),
  currencyType: text("currency_type").notNull().default("regular"), // regular or premium
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const userCardBacks = pgTable("user_card_backs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  cardBackId: integer("card_back_id")
    .notNull()
    .references(() => cardBacks.id),
  dateAcquired: timestamp("date_acquired").notNull().defaultNow(),
  isActive: boolean("is_active").notNull().default(false),
  isDefault: boolean("is_default").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertCardBackSchema = createInsertSchema(cardBacks)
  .pick({
    name: true,
    imageUrl: true,
  })
  .extend({
    name: z.string().min(1, "Card back name is required").max(50, "Card back name too long"),
    imageUrl: z.string().min(1, "Image URL is required"),
    isActive: z.boolean().default(true),
    price: z.number().default(0),
    currencyType: z.enum(["regular", "premium"]).default("regular"),
  });

// Add relations
export const cardBacksRelations = relations(cardBacks, ({ many }) => ({
  userCardBacks: many(userCardBacks),
}));

export const userCardBacksRelations = relations(userCardBacks, ({ one }) => ({
  user: one(users, {
    fields: [userCardBacks.userId],
    references: [users.id],
  }),
  cardBack: one(cardBacks, {
    fields: [userCardBacks.cardBackId],
    references: [cardBacks.id],
  }),
}));

// Add type exports
export type CardBack = typeof cardBacks.$inferSelect;
export type UserCardBack = typeof userCardBacks.$inferSelect;
export type InsertCardBack = z.infer<typeof insertCardBackSchema>;

// Add type exports
export type CurrencyTransaction = typeof currencyTransactions.$inferSelect;
export type InsertCurrencyTransaction = z.infer<typeof insertCurrencyTransactionSchema>;
export type ShopItem = typeof shopItems.$inferSelect;
export type InsertShopItem = z.infer<typeof insertShopItemSchema>;
export type UserPurchase = typeof userPurchases.$inferSelect;
export type InsertUserPurchase = z.infer<typeof insertUserPurchaseSchema>;
export type DailyReward = typeof dailyRewards.$inferSelect;
export type InsertDailyReward = z.infer<typeof insertDailyRewardSchema>;
export type SupportTicket = typeof supportTickets.$inferSelect;
export type InsertSupportTicket = z.infer<typeof insertSupportTicketSchema>;
export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type PendingPayment = typeof pendingPayments.$inferSelect;
export type CoinPackage = typeof coinPackages.$inferSelect;
export type InsertCoinPackage = z.infer<typeof insertCoinPackageSchema>;

// Base game settings type that is common across all game modes
export const baseGameSettings = {
  aiDifficulty: z.object({
    beginner: z.number(),
    pro: z.number(),
    default: z.number(),
  }),
  shuffleType: z.enum(["half", "full"]),
  deckType: z.string(),
  gbaaProtection: z.boolean(),
  gbaaPenalty: z.object({
    canCall: z.boolean(),
    true: z.number(),
    false: z.number(),
  }),
  winnerStart: z.boolean(),
  dryMode: z.boolean(),
  underTenEnabled: z.boolean(),
  gameScore: z.object({
    numberOfRounds: z.number(),
    pointsToWin: z.number(),
    direction: z.enum(["asc", "desc"]),
  }),
  searchTimeOut: z.number(),
  timer: z.object({
    timerEnabled: z.boolean(),
    timerDuration: z.number(),
    timerWarning: z.number(),
    timerWarningEnabled: z.boolean(),
  }),
  inactivityTimeoutTime: z.number(),
};

// Game settings tables for each mode
export const rankedGameSettings = pgTable("ranked_game_settings", {
  id: serial("id").primaryKey(),
  updatedBy: integer("updated_by")
    .notNull()
    .references(() => users.id),
  isActive: boolean("is_active").notNull().default(true),
  aiDifficultyBeginner: integer("ai_difficulty_beginner").notNull(),
  aiDifficultyPro: integer("ai_difficulty_pro").notNull(),
  aiDifficultyDefault: integer("ai_difficulty_default").notNull(),
  shuffleType: text("shuffle_type").notNull(),
  deckType: text("deck_type").notNull(),
  gbaaProtection: boolean("gbaa_protection").notNull(),
  gbaaPenaltyCanCall: boolean("gbaa_penalty_can_call").notNull(),
  gbaaPenaltyTrue: integer("gbaa_penalty_true").notNull(),
  gbaaPenaltyFalse: integer("gbaa_penalty_false").notNull(),
  winnerStart: boolean("winner_start").notNull(),
  dryMode: boolean("dry_mode").notNull(),
  underTenEnabled: boolean("under_ten_enabled").notNull(),
  gameScoreNumberOfRounds: integer("game_score_number_of_rounds").notNull(),
  gameScorePointsToWin: integer("game_score_points_to_win").notNull(),
  gameScoreDirection: text("game_score_direction").notNull(),
  searchTimeOut: integer("search_time_out").notNull(),
  timerEnabled: boolean("timer_enabled").notNull(),
  timerDuration: integer("timer_duration").notNull(),
  timerWarning: integer("timer_warning").notNull(),
  timerWarningEnabled: boolean("timer_warning_enabled").notNull(),
  inactivityTimeoutTime: integer("inactivity_timeout_time").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const quickPlayGameSettings = pgTable("quick_play_game_settings", {
  id: serial("id").primaryKey(),
  updatedBy: integer("updated_by")
    .notNull()
    .references(() => users.id),
  isActive: boolean("is_active").notNull().default(true),
  aiDifficultyBeginner: integer("ai_difficulty_beginner").notNull(),
  aiDifficultyPro: integer("ai_difficulty_pro").notNull(),
  aiDifficultyDefault: integer("ai_difficulty_default").notNull(),
  shuffleType: text("shuffle_type").notNull(),
  deckType: text("deck_type").notNull(),
  gbaaProtection: boolean("gbaa_protection").notNull(),
  gbaaPenaltyCanCall: boolean("gbaa_penalty_can_call").notNull(),
  gbaaPenaltyTrue: integer("gbaa_penalty_true").notNull(),
  gbaaPenaltyFalse: integer("gbaa_penalty_false").notNull(),
  winnerStart: boolean("winner_start").notNull(),
  dryMode: boolean("dry_mode").notNull(),
  underTenEnabled: boolean("under_ten_enabled").notNull(),
  gameScoreNumberOfRounds: integer("game_score_number_of_rounds").notNull(),
  gameScorePointsToWin: integer("game_score_points_to_win").notNull(),
  gameScoreDirection: text("game_score_direction").notNull(),
  searchTimeOut: integer("search_time_out").notNull(),
  timerEnabled: boolean("timer_enabled").notNull(),
  timerDuration: integer("timer_duration").notNull(),
  timerWarning: integer("timer_warning").notNull(),
  timerWarningEnabled: boolean("timer_warning_enabled").notNull(),
  inactivityTimeoutTime: integer("inactivity_timeout_time").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const customGameSettings = pgTable("custom_game_settings", {
  id: serial("id").primaryKey(),
  updatedBy: integer("updated_by")
    .notNull()
    .references(() => users.id),
  isActive: boolean("is_active").notNull().default(true),
  aiDifficultyBeginner: integer("ai_difficulty_beginner").notNull(),
  aiDifficultyPro: integer("ai_difficulty_pro").notNull(),
  aiDifficultyDefault: integer("ai_difficulty_default").notNull(),
  shuffleType: text("shuffle_type").notNull(),
  deckType: text("deck_type").notNull(),
  gbaaProtection: boolean("gbaa_protection").notNull(),
  gbaaPenaltyCanCall: boolean("gbaa_penalty_can_call").notNull(),
  gbaaPenaltyTrue: integer("gbaa_penalty_true").notNull(),
  gbaaPenaltyFalse: integer("gbaa_penalty_false").notNull(),
  winnerStart: boolean("winner_start").notNull(),
  dryMode: boolean("dry_mode").notNull(),
  underTenEnabled: boolean("under_ten_enabled").notNull(),
  gameScoreNumberOfRounds: integer("game_score_number_of_rounds").notNull(),
  gameScorePointsToWin: integer("game_score_points_to_win").notNull(),
  gameScoreDirection: text("game_score_direction").notNull(),
  searchTimeOut: integer("search_time_out").notNull(),
  timerEnabled: boolean("timer_enabled").notNull(),
  timerDuration: integer("timer_duration").notNull(),
  timerWarning: integer("timer_warning").notNull(),
  timerWarningEnabled: boolean("timer_warning_enabled").notNull(),
  inactivityTimeoutTime: integer("inactivity_timeout_time").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const aiGameSettings = pgTable("ai_game_settings", {
  id: serial("id").primaryKey(),
  updatedBy: integer("updated_by")
    .notNull()
    .references(() => users.id),
  isActive: boolean("is_active").notNull().default(true),
  aiDifficultyBeginner: integer("ai_difficulty_beginner").notNull(),
  aiDifficultyPro: integer("ai_difficulty_pro").notNull(),
  aiDifficultyDefault: integer("ai_difficulty_default").notNull(),
  shuffleType: text("shuffle_type").notNull(),
  deckType: text("deck_type").notNull(),
  gbaaProtection: boolean("gbaa_protection").notNull(),
  gbaaPenaltyCanCall: boolean("gbaa_penalty_can_call").notNull(),
  gbaaPenaltyTrue: integer("gbaa_penalty_true").notNull(),
  gbaaPenaltyFalse: integer("gbaa_penalty_false").notNull(),
  winnerStart: boolean("winner_start").notNull(),
  dryMode: boolean("dry_mode").notNull(),
  underTenEnabled: boolean("under_ten_enabled").notNull(),
  gameScoreNumberOfRounds: integer("game_score_number_of_rounds").notNull(),
  gameScorePointsToWin: integer("game_score_points_to_win").notNull(),
  gameScoreDirection: text("game_score_direction").notNull(),
  searchTimeOut: integer("search_time_out").notNull(),
  timerEnabled: boolean("timer_enabled").notNull(),
  timerDuration: integer("timer_duration").notNull(),
  timerWarning: integer("timer_warning").notNull(),
  timerWarningEnabled: boolean("timer_warning_enabled").notNull(),
  inactivityTimeoutTime: integer("inactivity_timeout_time").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const tournamentGameSettings = pgTable("tournament_game_settings", {
  id: serial("id").primaryKey(),
  updatedBy: integer("updated_by")
    .notNull()
    .references(() => users.id),
  isActive: boolean("is_active").notNull().default(true),
  aiDifficultyBeginner: integer("ai_difficulty_beginner").notNull(),
  aiDifficultyPro: integer("ai_difficulty_pro").notNull(),
  aiDifficultyDefault: integer("ai_difficulty_default").notNull(),
  shuffleType: text("shuffle_type").notNull(),
  deckType: text("deck_type").notNull(),
  gbaaProtection: boolean("gbaa_protection").notNull(),
  gbaaPenaltyCanCall: boolean("gbaa_penalty_can_call").notNull(),
  gbaaPenaltyTrue: integer("gbaa_penalty_true").notNull(),
  gbaaPenaltyFalse: integer("gbaa_penalty_false").notNull(),
  winnerStart: boolean("winner_start").notNull(),
  dryMode: boolean("dry_mode").notNull(),
  underTenEnabled: boolean("under_ten_enabled").notNull(),
  gameScoreNumberOfRounds: integer("game_score_number_of_rounds").notNull(),
  gameScorePointsToWin: integer("game_score_points_to_win").notNull(),
  gameScoreDirection: text("game_score_direction").notNull(),
  searchTimeOut: integer("search_time_out").notNull(),
  timerEnabled: boolean("timer_enabled").notNull(),
  timerDuration: integer("timer_duration").notNull(),
  timerWarning: integer("timer_warning").notNull(),
  timerWarningEnabled: boolean("timer_warning_enabled").notNull(),
  inactivityTimeoutTime: integer("inactivity_timeout_time").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Add types for all game settings
export type RankedGameSettings = typeof rankedGameSettings.$inferSelect;
export type QuickPlayGameSettings = typeof quickPlayGameSettings.$inferSelect;
export type CustomGameSettings = typeof customGameSettings.$inferSelect;
export type AIGameSettings = typeof aiGameSettings.$inferSelect;
export type TournamentGameSettings = typeof tournamentGameSettings.$inferSelect;

// Add relations between settings and users
export const rankedGameSettingsRelations = relations(rankedGameSettings, ({ one }) => ({
  updatedByUser: one(users, {
    fields: [rankedGameSettings.updatedBy],
    references: [users.id],
  }),
}));

export const quickPlayGameSettingsRelations = relations(quickPlayGameSettings, ({ one }) => ({
  updatedByUser: one(users, {
    fields: [quickPlayGameSettings.updatedBy],
    references: [users.id],
  }),
}));

export const customGameSettingsRelations = relations(customGameSettings, ({ one }) => ({
  updatedByUser: one(users, {
    fields: [customGameSettings.updatedBy],
    references: [users.id],
  }),
}));

export const aiGameSettingsRelations = relations(aiGameSettings, ({ one }) => ({
  updatedByUser: one(users, {
    fields: [aiGameSettings.updatedBy],
    references: [users.id],
  }),
}));

export const tournamentGameSettingsRelations = relations(tournamentGameSettings, ({ one }) => ({
  updatedByUser: one(users, {
    fields: [tournamentGameSettings.updatedBy],
    references: [users.id],
  }),
}));

// Insert schemas for game settings
export const insertGameSettingsSchema = z.object({
  aiDifficultyBeginner: z.number(),
  aiDifficultyPro: z.number(),
  aiDifficultyDefault: z.number(),
  shuffleType: z.enum(["half", "full"]),
  deckType: z.string(),
  gbaaProtection: z.boolean(),
  gbaaPenaltyCanCall: z.boolean(),
  gbaaPenaltyTrue: z.number(),
  gbaaPenaltyFalse: z.number(),
  winnerStart: z.boolean(),
  dryMode: z.boolean(),
  underTenEnabled: z.boolean(),
  gameScoreNumberOfRounds: z.number(),
  gameScorePointsToWin: z.number(),
  gameScoreDirection: z.enum(["asc", "desc"]),
  searchTimeOut: z.number(),
  timerEnabled: z.boolean(),
  timerDuration: z.number(),
  timerWarning: z.number(),
  timerWarningEnabled: z.boolean(),
  inactivityTimeoutTime: z.number(),
  updatedBy: z.number(),
});

export type InsertGameSettings = z.infer<typeof insertGameSettingsSchema>;

export const defaultAvatars = pgTable("default_avatars", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  imageUrl: text("image_url").notNull(),
  category: text("category").notNull().default("basic"), // basic, premium, special, etc.
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const defaultBanners = pgTable("default_banners", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  imageUrl: text("image_url").notNull(),
  category: text("category").notNull().default("basic"), // basic, premium, special, etc.
  isActive: boolean("is_active").notNull().default(true),
  shopItemId: integer("shop_item_id").references(() => shopItems.id), // Reference to the shop item if it's for sale
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Add relations for defaultBanners
export const defaultBannersRelations = relations(defaultBanners, ({ one }) => ({
  shopItem: one(shopItems, {
    fields: [defaultBanners.shopItemId],
    references: [shopItems.id],
  }),
}));

// Add insert schemas for default avatars and banners
export const insertDefaultAvatarSchema = createInsertSchema(defaultAvatars)
  .pick({
    name: true,
    imageUrl: true,
    category: true,
  })
  .extend({
    name: z.string().min(1, "Avatar name is required").max(50, "Avatar name too long"),
    imageUrl: z.string().min(1, "Image URL is required"),
    category: z.enum(["basic", "premium", "special"]).default("basic"),
  });

export const insertDefaultBannerSchema = createInsertSchema(defaultBanners)
  .pick({
    name: true,
    imageUrl: true,
    category: true,
  })
  .extend({
    name: z.string().min(1, "Banner name is required").max(50, "Banner name too long"),
    imageUrl: z.string().min(1, "Image URL is required"),
    category: z.enum(["basic", "premium", "special"]).default("basic"),
    shopItemId: z.number().optional(),
  });

// Add type exports
export type DefaultAvatar = typeof defaultAvatars.$inferSelect;
export type DefaultBanner = typeof defaultBanners.$inferSelect;
export type InsertDefaultAvatar = z.infer<typeof insertDefaultAvatarSchema>;
export type InsertDefaultBanner = z.infer<typeof insertDefaultBannerSchema>;

export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  title: text("title").notNull(),
  message: text("message").notNull(),
  type: text("type").notNull(), // game_invite, friend_request, match_found, game_turn, achievement, system, etc.
  isRead: boolean("is_read").notNull().default(false),
  actionUrl: text("action_url"), // Optional URL to navigate to when clicked
  relatedId: integer("related_id"), // Optional related entity ID (e.g., game ID, friend request ID)
  relatedType: text("related_type"), // Optional related entity type (e.g., game, friend_request)
  metadata: jsonb("metadata").default({}), // Optional additional data
  expiresAt: timestamp("expires_at"), // Optional expiration date
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
}));

export const insertNotificationSchema = createInsertSchema(notifications).pick({
  userId: true,
  title: true,
  message: true,
  type: true,
  actionUrl: true,
  relatedId: true,
  relatedType: true,
  metadata: true,
  expiresAt: true,
}).extend({
  title: z.string().min(1, "Title is required"),
  message: z.string().min(1, "Message is required"),
  type: z.string().min(1, "Type is required"),
});

export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;

// Device tokens table for push notifications
export const deviceTokens = pgTable("device_tokens", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  token: text("token").notNull().unique(),
  platform: text("platform").notNull(), // ios, android
  deviceName: text("device_name"),
  isActive: boolean("is_active").notNull().default(true),
  lastUsed: timestamp("last_used").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const deviceTokensRelations = relations(deviceTokens, ({ one }) => ({
  user: one(users, {
    fields: [deviceTokens.userId],
    references: [users.id],
  }),
}));

export const insertDeviceTokenSchema = createInsertSchema(deviceTokens).pick({
  userId: true,
  token: true,
  platform: true,
  deviceName: true,
}).extend({
  token: z.string().min(1, "Token is required"),
  platform: z.enum(["ios", "android"]),
  deviceName: z.string().optional(),
});

export type DeviceToken = typeof deviceTokens.$inferSelect;
export type InsertDeviceToken = z.infer<typeof insertDeviceTokenSchema>;

// Announcements table for in-app announcements
export const announcements = pgTable("announcements", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  content: text("content").notNull(),
  type: text("type").notNull().default("info"), // info, warning, critical, promo, update
  startDate: timestamp("start_date").notNull().defaultNow(),
  endDate: timestamp("end_date").notNull(),
  targetMemberships: jsonb("target_memberships").notNull().default(["all"]), // Array of membership types: all, free, premium, vip
  dismissible: boolean("dismissible").notNull().default(true),
  dismissCount: integer("dismiss_count").notNull().default(3), // How many times can user dismiss before permanent
  imageUrl: text("image_url"),
  actionUrl: text("action_url"), // Deep link or external URL
  priority: integer("priority").notNull().default(1), // Higher numbers shown first
  isActive: boolean("is_active").notNull().default(false), // Control whether the announcement is active
  createdBy: integer("created_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const announcementReadStatus = pgTable("announcement_read_status", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  announcementId: integer("announcement_id")
    .notNull()
    .references(() => announcements.id),
  read: boolean("read").notNull().default(false),
  dismissedUntil: timestamp("dismissed_until"), // Temporarily dismissed
  permanentlyDismissed: boolean("permanently_dismissed").notNull().default(false),
  dismissCount: integer("dismiss_count").notNull().default(0), // How many times user has dismissed this announcement
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Define relationships
export const announcementsRelations = relations(announcements, ({ one, many }) => ({
  creator: one(users, {
    fields: [announcements.createdBy],
    references: [users.id],
  }),
  readStatus: many(announcementReadStatus),
}));

export const announcementReadStatusRelations = relations(announcementReadStatus, ({ one }) => ({
  user: one(users, {
    fields: [announcementReadStatus.userId],
    references: [users.id],
  }),
  announcement: one(announcements, {
    fields: [announcementReadStatus.announcementId],
    references: [announcements.id],
  }),
}));

// Insert schemas
export const insertAnnouncementSchema = createInsertSchema(announcements)
  .pick({
    title: true,
    content: true,
    type: true,
    startDate: true,
    endDate: true,
    dismissible: true,
    dismissCount: true,
    imageUrl: true,
    actionUrl: true,
    priority: true,
    createdBy: true,
  })
  .extend({
    title: z.string().min(1, "Title is required").max(100, "Title is too long"),
    content: z.string().min(1, "Content is required"),
    type: z.enum(["info", "warning", "critical", "promo", "update"]).default("info"),
    targetMemberships: z.array(z.enum(["all", "free", "premium", "vip"])).default(["all"]),
    dismissCount: z.number().min(1).max(10).default(3),
    priority: z.number().min(1).max(10).default(1),
    isActive: z.boolean().default(true),
  });

export const insertAnnouncementReadStatusSchema = createInsertSchema(announcementReadStatus)
  .pick({
    userId: true,
    announcementId: true,
    read: true,
    dismissedUntil: true,
    permanentlyDismissed: true,
    dismissCount: true,
  });

// Add type exports
export type Announcement = typeof announcements.$inferSelect;
export type AnnouncementReadStatus = typeof announcementReadStatus.$inferSelect;
export type InsertAnnouncement = z.infer<typeof insertAnnouncementSchema>;
export type InsertAnnouncementReadStatus = z.infer<typeof insertAnnouncementReadStatusSchema>;

export const userGameSettings = pgTable("user_game_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id),
  backgroundBoardId: integer("background_board_id").references(() => shopItems.id), // Background board ID for game rooms
  isDefault: boolean("is_default").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const levels = pgTable("levels", {
  id: serial("id").primaryKey(),
  level: integer("level").notNull().unique(),
  xpRequired: integer("xp_required").notNull(),
  badgeName: text("badge_name").notNull(),
  badgeImageUrl: text("badge_image_url").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertLevelSchema = createInsertSchema(levels).pick({
  level: true,
  xpRequired: true,
  badgeName: true,
  badgeImageUrl: true,
});

export type Level = typeof levels.$inferSelect;
export type InsertLevel = z.infer<typeof insertLevelSchema>;


export const badgeTiers = pgTable("badge_tiers", {
  id: serial("id").primaryKey(),
  minLevel: integer("min_level").notNull(),
  maxLevel: integer("max_level").notNull(),
  name: text("name").notNull(),
  imageUrl: text("image_url").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertBadgeTierSchema = createInsertSchema(badgeTiers).pick({
  minLevel: true,
  maxLevel: true,
  name: true,
  imageUrl: true,
});

export type BadgeTier = typeof badgeTiers.$inferSelect;
export type InsertBadgeTier = z.infer<typeof insertBadgeTierSchema>;

// App info table for version management
export const appInfo = pgTable("app_info", {
  id: serial("id").primaryKey(),
  appVersion: text("app_version").notNull(),
  versionDate: timestamp("version_date").notNull().defaultNow(),
  forceUpdate: boolean("force_update").notNull().default(false),
  isCurrent: boolean("is_current").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertAppInfoSchema = createInsertSchema(appInfo)
  .pick({
    appVersion: true,
    forceUpdate: true,
    isCurrent: true,
  })
  .extend({
    appVersion: z.string().min(1, "Version is required"),
  });

export type AppInfo = typeof appInfo.$inferSelect;
export type InsertAppInfo = z.infer<typeof insertAppInfoSchema>;

// Player awards table for the award system
export const playerAwards = pgTable("player_awards", {
  id: serial("id").primaryKey(),
  playerId: integer("player_id").notNull().references(() => users.id),
  awardType: text("award_type").notNull(), // 'CURRENCY', 'DECK', 'SHOP_ITEM'
  currencyType: text("currency_type"), // 'COINS', 'GEMS', null if awardType is not CURRENCY
  amount: integer("amount"), // Amount of currency, null if awardType is not CURRENCY
  deckId: integer("deck_id").references(() => cardDecks.id), // null if awardType is not DECK
  shopItemId: integer("shop_item_id").references(() => shopItems.id), // null if awardType is not SHOP_ITEM
  awardedByAdminId: integer("awarded_by_admin_id").references(() => users.id), // Optional: for admin tracking
  reason: text("reason"), // Optional: for admin notes
  isClaimed: boolean("is_claimed").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  claimedAt: timestamp("claimed_at"),
});

export const playerAwardsRelations = relations(playerAwards, ({ one }) => ({
  player: one(users, {
    fields: [playerAwards.playerId],
    references: [users.id],
  }),
  awardedByAdmin: one(users, {
    fields: [playerAwards.awardedByAdminId],
    references: [users.id],
  }),
  deck: one(cardDecks, {
    fields: [playerAwards.deckId],
    references: [cardDecks.id],
  }),
  shopItem: one(shopItems, {
    fields: [playerAwards.shopItemId],
    references: [shopItems.id],
  }),
}));

export type PlayerAward = typeof playerAwards.$inferSelect;
export const insertPlayerAwardSchema = createInsertSchema(playerAwards);
export type InsertPlayerAward = z.infer<typeof insertPlayerAwardSchema>;

